import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Banner {
  id: string;
  title: string;
  content: string;
}

export function PromotionalBanner() {
  const [banner, setBanner] = useState<Banner | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    // Load dismissed banners from localStorage
    const savedDismissed = localStorage.getItem('dismissed_banners');
    if (savedDismissed) {
      setDismissed(JSON.parse(savedDismissed));
    }
  }, []);

  useEffect(() => {
    const fetchActiveBanner = async () => {
      const { data, error } = await supabase
        .from("promotional_banners")
        .select("id, title, content")
        .eq("is_active", true)
        .or('expires_at.is.null,expires_at.gt.now()')
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (!error && data && !dismissed.includes(data.id)) {
        setBanner(data);
      }
    };

    fetchActiveBanner();

    // Realtime subscription for banner updates
    const channel = supabase
      .channel("promotional-banners")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "promotional_banners",
        },
        () => {
          fetchActiveBanner();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dismissed]);

  const handleDismiss = () => {
    if (banner) {
      const newDismissed = [...dismissed, banner.id];
      setDismissed(newDismissed);
      localStorage.setItem('dismissed_banners', JSON.stringify(newDismissed));
      setBanner(null);
    }
  };

  if (!banner) return null;

  return (
    <div className="relative bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border border-primary/30 rounded-xl p-4 mb-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
          <Megaphone size={16} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif font-medium text-foreground text-sm mb-1">
            {banner.title}
          </h3>
          <p className="text-xs text-muted-foreground whitespace-pre-wrap">
            {banner.content}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={handleDismiss}
        >
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}
